/**
 * HAND-ONDERHOUDEN, SYNC-VEILIGE overrides voor de rooster-BEDIENING (koord/stang) — nooit door
 * `npm run sync:all` overschreven. Sommige roosters nóemen in hun Timmers-beschrijving een koord-/
 * stangbediening, maar de API levert er geen `cordPrice`/`rodPrice` bij, waardoor de bediening-optie
 * ten onrechte ontbreekt. Hier zetten we 'm alsnog aan:
 *   - met een vaste netto-prijs (cent) als die bekend is (Duco-standaard: koord 665 / stang 1024), of
 *   - `quote: true` = bediening wél beschikbaar maar prijs OP AANVRAAG (kiezen → regel wordt offerte).
 */
export interface RoosterBedieningOverride {
  /** Koord-prijs (cent, netto). Weglaten = uit de data/geen koord. */
  cordCents?: number;
  /** Stang-prijs (cent, netto). Weglaten = uit de data/geen stang. */
  rodCents?: number;
  /** true = bediening beschikbaar maar prijs op aanvraag (koord + stang), tot de echte prijs bekend is. */
  quote?: boolean;
}

export const ROOSTER_BEDIENING_OVERRIDES: Record<string, RoosterBedieningOverride> = {
  // Duco-roosters → standaard Duco-bedieningsprijs (koord €6,65 / stang €10,24 netto). Deze
  // roosters leveren geen cordPrice/rodPrice via de API, maar hebben wél de Duco-standaardprijs.
  "ducoton-10-zr": { cordCents: 665, rodCents: 1024 },
  "ducotwin-50zr-fix": { cordCents: 665, rodCents: 1024 },
  "ducotwin-120zr-ak-": { cordCents: 665, rodCents: 1024 },
  "glasvent-16-zr-ak": { cordCents: 665, rodCents: 1024 },
  // Andere merken: bediening wél mogelijk, prijs nog niet bekend → op aanvraag.
  "multi-air": { quote: true },
  "renson-junior-150-epc-design": { quote: true },
};

/** Effectieve koord-prijs (cent): override-prijs > op-aanvraag (0) > data. null = geen koord. */
export function effCordCents(slug: string, dataCord: number | null | undefined): number | null {
  const ov = ROOSTER_BEDIENING_OVERRIDES[slug];
  if (ov?.cordCents != null) return ov.cordCents;
  if (ov?.quote) return 0; // aanwezig, prijs op aanvraag
  return dataCord ?? null;
}

/** Effectieve stang-prijs (cent): override-prijs > op-aanvraag (0) > data. null = geen stang. */
export function effRodCents(slug: string, dataRod: number | null | undefined): number | null {
  const ov = ROOSTER_BEDIENING_OVERRIDES[slug];
  if (ov?.rodCents != null) return ov.rodCents;
  if (ov?.quote) return 0; // aanwezig, prijs op aanvraag
  return dataRod ?? null;
}

/**
 * true = de bediening van dit rooster is op aanvraag (geen online prijs; kiezen → offerte-regel).
 * ELK rooster kan koord/stang krijgen: is er geen vaste prijs (override óf API-`cordPrice`/`rodPrice`),
 * dan tonen we de bediening tóch — op aanvraag. Alleen roosters met een bekende prijs zijn niet-quote.
 */
export function bedieningIsQuote(
  slug: string,
  dataCord?: number | null,
  dataRod?: number | null,
): boolean {
  const ov = ROOSTER_BEDIENING_OVERRIDES[slug];
  if (ov?.quote) return true;
  if (ov?.cordCents != null || ov?.rodCents != null) return false; // vaste override-prijs
  return (dataCord ?? null) == null && (dataRod ?? null) == null; // geen prijs bekend → op aanvraag
}

/**
 * Vanaf deze lengte levert Duco het rooster in twee delen (Prijslijst Duco 01.05.2026, pag. 22).
 * Het binnenwerk wordt daarbij in het midden gesplitst, en elke helft is afzonderlijk bedienbaar.
 *
 * Stond eerder alleen client-side (features/product/roosterSpecs.ts). Hij hoort hier omdat de
 * server dezelfde grens moet kennen: hij bepaalt hoevéél bedieningen er besteld en gerekend worden.
 */
export function roosterSplitAtMm(slug: string): number | null {
  if (/skymax/i.test(slug)) return 1000;
  if (/glasmax|glasvent|topvent|skyvent|minimax|ducomax/i.test(slug)) return 2000;
  if (/duco(top|fit|ton|klep|line|flat)|easyfit|ducosmart/i.test(slug)) return 1500;
  return null;
}

/**
 * Vraagt dit rooster verplicht TWEE bedieningen (links + rechts)?
 *
 * Vanaf de splitsingslengte gaat het binnenwerk in tweeën en is elke helft apart bedienbaar — dus
 * heb je er twee nodig, één per helft. Dat geldt voor KOORD net zo goed als voor stang: het zit
 * niet in het type bediening maar in de gedeelde klep.
 *
 * "Links" of "rechts" alleen is dan geen keuze meer maar een order die de fabriek niet kan
 * uitvoeren: de configurator biedt alleen nog "beide zijden (2×)" aan, en de server rekent en
 * bestelt er ook twee.
 *
 * De grens is dezelfde als die van de splitsingsmelding op de pagina (vanaf, dus inclusief). Zou
 * die twee uit elkaar lopen, dan zou een rooster van precies 1.500 mm melden dat het gedeeld is en
 * tegelijk één bediening aanbieden.
 */
export function bedieningVereistBeideZijden(
  slug: string,
  bediening: string | undefined,
  widthMm: number | undefined,
): boolean {
  if (bediening !== "koord" && bediening !== "stang") return false;
  if (typeof widthMm !== "number") return false;
  const splitAt = roosterSplitAtMm(slug);
  return splitAt != null && widthMm >= splitAt;
}
